import "./style.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowDown,
  Friends,
  HomeActive,
  Logo,
  Market,
  Messenger,
  Notifications,
  Search,
  Watch,
} from "../../svg";
import { useSelector } from "react-redux";
import { useState, useEffect, useRef } from "react";
import { search, getSearchHistory } from "../../functions/user";
import { useToast } from "../ToastContainer";
import axios from "axios";
// AllMenu removed (not used in this header variant)
import useClickOutside from "../../helpers/clickOutside";
import UserMenu from "./userMenu";
import { useNotifications } from "../../context/NotificationContext";
import { useMessageNotifications } from "../../context/MessageNotificationContext";
import LiveStreamViewer from "../LiveStreamViewer";
// Cookies removed (dark mode removed)

const USER_MENU_OUTSIDE_IGNORE_SELECTORS = [".logout_confirm_overlay", ".logout_confirm_modal"];

// Utility function to format relative time (compact version for header)
const formatRelativeTime = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Just now';
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }
  
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks}w ago`;
  }
  
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths}mo ago`;
  }
  
  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears}y ago`;
};

const formatMobileNotificationMessage = (message, appLabel = 'Birthday Reminder 🎉') => {
  if (!message) return appLabel;

  const domainPattern = /(?:https?:\/\/|www\.)[^\s]+|localhost:\d+/gi;
  if (domainPattern.test(message)) {
    return message.replace(domainPattern, appLabel);
  }

  return message;
};

export default function Header({ showMessengerSidebar = false, onMessengerToggle = () => {} }) {
  const { user } = useSelector((state) => ({ ...state }));
  const color = "#65676b";

  const location = useLocation();
  const currentPath = location.pathname;
  const navigate = useNavigate();
  const { showToast, showSuccess, showError } = useToast();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [inputFocused, setInputFocused] = useState(false);
  const inputRef = useRef(null);
  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedLiveStream, setSelectedLiveStream] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileNotifications, setShowMobileNotifications] = useState(false);
  const [showMobileUserMenu, setShowMobileUserMenu] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const languageSelectorRef = useRef(null);
  const mobileLanguageSelectorRef = useRef(null);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    // Get saved language from localStorage, but if none exists, check if Google Translate has a preference
    const savedLang = localStorage.getItem('selectedLanguage');
    
    if (savedLang) {
      return savedLang;
    }
    
    // Check if there's an existing Google Translate cookie
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'googtrans' && value) {
        // Extract language from cookie format: /en/lang
        const parts = value.split('/');
        if (parts.length >= 3 && parts[2] !== 'en') {
          return parts[2];
        }
      }
    }
    
    // Default to English if no preference found
    return 'en';
  });

  // Clear active search text/suggestions when navigating between pages.
  useEffect(() => {
    setSearchQuery("");
    setResults([]);
    setInputFocused(false);
    setShowMobileSearch(false);
  }, [currentPath]);
  
  // Use real-time notifications from context
  const { 
    notifications, 
    unreadCount, 
    markAllAsRead
  } = useNotifications();
  const { unreadMessageCount } = useMessageNotifications();

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Manage body scroll when mobile search is open
  useEffect(() => {
    if (isMobile && showMobileSearch) {
      document.body.classList.add('mobile_search_open');
    } else {
      document.body.classList.remove('mobile_search_open');
    }
    
    return () => {
      document.body.classList.remove('mobile_search_open');
    };
  }, [isMobile, showMobileSearch]);

  // Close mobile menu when clicking outside
  const mobileMenuRef = useRef(null);
  useClickOutside(mobileMenuRef, () => setShowMobileMenu(false));
  
  // Close mobile user menu when clicking outside
  const mobileUserMenuRef = useRef(null);
  useClickOutside(mobileUserMenuRef, () => setShowMobileUserMenu(false));
  
  // Close mobile search when clicking outside
  const mobileSearchRef = useRef(null);
  useClickOutside(mobileSearchRef, () => setShowMobileSearch(false));

  // Close language selector when clicking outside
  useClickOutside(languageSelectorRef, () => setShowLanguageSelector(false));
  useClickOutside(mobileLanguageSelectorRef, () => setShowLanguageSelector(false));

  // Hide Google Translate banner whenever selected language changes
  useEffect(() => {
    const hideBanner = () => {
      const allTranslateElements = document.querySelectorAll(
        '.goog-te-banner-frame, iframe.goog-te-banner-frame, .goog-te-menu-frame, .goog-te-gadget, .goog-te-gadget-simple, .goog-te-balloon-frame'
      );
      allTranslateElements.forEach(el => {
        if (el) {
          el.style.display = 'none';
          el.style.visibility = 'hidden';
          el.style.height = '0';
          el.style.width = '0';
          el.style.position = 'absolute';
          el.style.left = '-9999px';
          el.style.opacity = '0';
        }
      });
      document.body.style.top = '0';
      document.body.style.marginTop = '0';
      document.documentElement.style.marginTop = '0';
    };
    
    // Hide immediately when language changes
    hideBanner();
    
    // Set up observer to hide banner whenever it appears
    const observer = new MutationObserver(() => {
      hideBanner();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
    
    // Also check periodically
    const interval = setInterval(hideBanner, 50);
    
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [selectedLanguage]);

  // Initialize Google Translate
  useEffect(() => {
    // Get saved language preference
    const savedLang = localStorage.getItem('selectedLanguage') || 'en';
    
    // Force clear any existing Google Translate state first (current host + parent domain if any)
    document.cookie = 'googtrans=; path=/; max-age=0; SameSite=Lax';
    const initHostname = typeof window !== 'undefined' ? window.location.hostname : '';
    if (initHostname && initHostname.includes('.')) {
      try {
        document.cookie = `googtrans=; path=/; domain=.${initHostname.split('.').slice(-2).join('.')}; max-age=0; SameSite=Lax`;
      } catch (e) { /* ignore */ }
    }
    
    // Clear any Google Translate storage
    if (window.localStorage && window.localStorage.getItem('google_translate')) {
      window.localStorage.removeItem('google_translate');
    }
    
    // Ensure Google Translate cookie is set correctly after clearing.
    // Use current host only (no domain) so it works on main site, test site, and any domain.
    const setTranslateCookie = (lang) => {
      // Clear existing Google Translate cookie for current host (works on any domain)
      document.cookie = 'googtrans=; path=/; max-age=0; SameSite=Lax';
      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      if (hostname && hostname.includes('.')) {
        const domainPart = hostname.split('.').slice(-2).join('.');
        try {
          document.cookie = `googtrans=; path=/; domain=.${domainPart}; max-age=0; SameSite=Lax`;
        } catch (e) { /* ignore */ }
      }
      
      if (lang !== 'en') {
        const cookieValue = `/en/${lang}`;
        document.cookie = `googtrans=${cookieValue}; path=/; max-age=31536000; SameSite=Lax`;
      }
      // If lang === 'en', cookie is already cleared above so Google shows original language
    };
    
    // Set cookie immediately, then again after a short delay to ensure it sticks
    setTranslateCookie(savedLang);
    setTimeout(() => setTranslateCookie(savedLang), 100);
    
    // Define the global init function before script loads
    window.googleTranslateElementInit = function () {
      if (window.google && window.google.translate) {
        new window.google.translate.TranslateElement(
          { 
            pageLanguage: 'en',
            includedLanguages: 'en,hi,kn,ml,te,ta,gu,pa,bn,ur,es,fr,de,it,pt,ru,zh-CN,ja,ko,ar,tr,vi,th,id,nl,pl,uk,cs,sv,no,da,fi,el,he,ro,hu,sk,bg,hr,sr,sl,et,lv,lt,sw,af,zu,xh,am,az,be,bs,ca,eu,gl,is,ga,mt,mk,sq',
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false
          },
          'google_translate_element_header'
        );
        
        // Apply saved language with timing that works without page refresh
        const applyLanguage = () => {
          const select = document.querySelector('.goog-te-combo');
          if (select) {
            select.classList.add('temporarily-visible');
            select.value = 'en';
            select.dispatchEvent(new Event('change', { bubbles: true }));
            select.dispatchEvent(new Event('input', { bubbles: true }));
            // Give the widget time to process before setting target language
            setTimeout(() => {
              select.value = savedLang;
              if (savedLang !== 'en') {
                select.dispatchEvent(new Event('change', { bubbles: true }));
                select.dispatchEvent(new Event('input', { bubbles: true }));
              }
              setTimeout(() => select.classList.remove('temporarily-visible'), 150);
            }, 300);
            return true;
          }
          return false;
        };

        // Retry with increasing delays so Translate iframe can be ready
        let attempts = 0;
        const maxAttempts = 20;
        const delays = [100, 200, 300, 500, 700, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 5000, 6000, 7000, 8000, 9000, 10000, 12000, 15000];
        
        const tryApplyLanguage = () => {
          if (applyLanguage()) {
            console.log(`Language ${savedLang} applied successfully after ${attempts + 1} attempts`);
          } else if (attempts < maxAttempts) {
            attempts++;
            // console.log(`Attempt ${attempts} failed for language ${savedLang}, retrying in ${delays[attempts - 1]}ms`);
            setTimeout(tryApplyLanguage, delays[attempts - 1]);
          } else {
            // Language apply failed after max attempts; avoid auto-reload to prevent 60s refresh loop
            console.warn(`Could not apply language ${savedLang} without reload; language may not be applied until next manual refresh.`);
          }
        };

        // Start trying to apply the language
        tryApplyLanguage();
      }
    };

    // Only add the script if not already present
    if (!document.getElementById('google-translate-script')) {
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    } else {
      // If script is already present, try to re-init
      if (window.google && window.google.translate) {
        window.googleTranslateElementInit();
      }
    }

    // Function to hide Google Translate banner and UI elements
    const hideGoogleTranslateBanner = () => {
      // Hide banner frame
      const banner = document.querySelector('.goog-te-banner-frame');
      if (banner) {
        banner.style.display = 'none';
        banner.style.visibility = 'hidden';
        banner.style.height = '0';
        banner.style.width = '0';
        banner.style.opacity = '0';
        banner.style.position = 'absolute';
        banner.style.left = '-9999px';
      }
      
      // Hide all Google Translate UI elements
      const translateElements = document.querySelectorAll(
        '.goog-te-banner-frame, iframe.goog-te-banner-frame, .goog-te-menu-frame, .goog-te-gadget, .goog-te-gadget-simple, .goog-te-balloon-frame, .goog-te-combo, #google_translate_element'
      );
      translateElements.forEach(el => {
        if (el) {
          el.style.display = 'none';
          el.style.visibility = 'hidden';
          el.style.height = '0';
          el.style.width = '0';
          el.style.position = 'absolute';
          el.style.left = '-9999px';
          el.style.opacity = '0';
        }
      });
      
      // Hide any Google Translate iframes
      const iframes = document.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        if (iframe.src && (iframe.src.includes('translate.googleapis.com') || iframe.src.includes('translate.google.com'))) {
          iframe.style.display = 'none';
          iframe.style.visibility = 'hidden';
          iframe.style.height = '0';
          iframe.style.width = '0';
          iframe.style.opacity = '0';
        }
      });
      
      // Reset body top position
      document.body.style.top = '0';
      document.body.style.marginTop = '0';
      document.documentElement.style.marginTop = '0';
    };

    // Hide banner immediately and set up observer to hide it when it appears
    hideGoogleTranslateBanner();
    
    // Use MutationObserver to hide banner whenever it appears
    const observer = new MutationObserver(() => {
      hideGoogleTranslateBanner();
    });
    
    // Observe body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Also check periodically - more frequently to catch banner immediately
    const hideInterval = setInterval(() => {
      hideGoogleTranslateBanner();
    }, 50);

    // Sync selected language with Google Translate after a delay
    const syncLanguage = () => {
      setTimeout(() => {
        const select = document.querySelector('.goog-te-combo');
        const savedLang = localStorage.getItem('selectedLanguage') || 'en';
        
        // Only sync if Google Translate doesn't match our saved language
        if (select && select.value !== savedLang) {
          // Set Google Translate to match our saved language
          select.value = savedLang;
          const event = new Event('change', { bubbles: true });
          select.dispatchEvent(event);
        }
      }, 1000);
    };
    syncLanguage();

    // Cleanup
    return () => {
      observer.disconnect();
      clearInterval(hideInterval);
    };
  }, []);

  // Language options
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'Hindi (हिन्दी)' },
    { code: 'kn', name: 'Kannada (ಕನ್ನಡ)' },
    { code: 'ml', name: 'Malayalam (മലയാളം)' },
    { code: 'te', name: 'Telugu (తెలుగు)' },
    { code: 'ta', name: 'Tamil (தமிழ்)' },
    { code: 'gu', name: 'Gujarati (ગુજરાતી)' },
    { code: 'pa', name: 'Punjabi (ਪੰਜਾਬੀ)' },
    { code: 'bn', name: 'Bengali (বাংলা)' },
    { code: 'ur', name: 'Urdu (اردو)' },
    { code: 'es', name: 'Spanish (Español)' },
    { code: 'fr', name: 'French (Français)' },
    { code: 'de', name: 'German (Deutsch)' },
    { code: 'it', name: 'Italian (Italiano)' },
    { code: 'pt', name: 'Portuguese (Português)' },
    { code: 'ru', name: 'Russian (Русский)' },
    { code: 'zh-CN', name: 'Chinese (中文)' },
    { code: 'ja', name: 'Japanese (日本語)' },
    { code: 'ko', name: 'Korean (한국어)' },
    { code: 'ar', name: 'Arabic (العربية)' },
    { code: 'tr', name: 'Turkish (Türkçe)' },
    { code: 'vi', name: 'Vietnamese (Tiếng Việt)' },
    { code: 'th', name: 'Thai (ไทย)' },
    { code: 'id', name: 'Indonesian (Bahasa Indonesia)' },
    { code: 'nl', name: 'Dutch (Nederlands)' },
    { code: 'pl', name: 'Polish (Polski)' },
    { code: 'uk', name: 'Ukrainian (Українська)' },
    { code: 'cs', name: 'Czech (Čeština)' },
    { code: 'sv', name: 'Swedish (Svenska)' },
    { code: 'no', name: 'Norwegian (Norsk)' },
    { code: 'da', name: 'Danish (Dansk)' },
    { code: 'fi', name: 'Finnish (Suomi)' },
    { code: 'el', name: 'Greek (Ελληνικά)' },
    { code: 'he', name: 'Hebrew (עברית)' },
    { code: 'ro', name: 'Romanian (Română)' },
    { code: 'hu', name: 'Hungarian (Magyar)' },
    { code: 'sk', name: 'Slovak (Slovenčina)' },
    { code: 'bg', name: 'Bulgarian (Български)' },
    { code: 'hr', name: 'Croatian (Hrvatski)' },
    { code: 'sr', name: 'Serbian (Српски)' },
    { code: 'sl', name: 'Slovenian (Slovenščina)' },
    { code: 'et', name: 'Estonian (Eesti)' },
    { code: 'lv', name: 'Latvian (Latviešu)' },
    { code: 'lt', name: 'Lithuanian (Lietuvių)' },
    { code: 'sw', name: 'Swahili (Kiswahili)' },
    { code: 'af', name: 'Afrikaans' },
    { code: 'zu', name: 'Zulu (isiZulu)' },
    { code: 'xh', name: 'Xhosa (isiXhosa)' },
    { code: 'am', name: 'Amharic (አማርኛ)' },
    { code: 'az', name: 'Azerbaijani (Azərbaycan)' },
    { code: 'be', name: 'Belarusian (Беларуская)' },
    { code: 'bs', name: 'Bosnian (Bosanski)' },
    { code: 'ca', name: 'Catalan (Català)' },
    { code: 'eu', name: 'Basque (Euskara)' },
    { code: 'gl', name: 'Galician (Galego)' },
    { code: 'is', name: 'Icelandic (Íslenska)' },
    { code: 'ga', name: 'Irish (Gaeilge)' },
    { code: 'mt', name: 'Maltese (Malti)' },
    { code: 'mk', name: 'Macedonian (Македонски)' },
    { code: 'sq', name: 'Albanian (Shqip)' },
  ];

  const handleLanguageChange = (langCode) => {
    // Update selected language state and save to localStorage
    setSelectedLanguage(langCode);
    localStorage.setItem('selectedLanguage', langCode);
    
    // Handle Google Translate cookie for current host (works on main site, test site, any domain)
    if (langCode === 'en') {
      document.cookie = 'googtrans=; path=/; max-age=0; SameSite=Lax';
      const hostname = window.location.hostname;
      if (hostname && hostname.includes('.')) {
        try {
          const domainPart = hostname.split('.').slice(-2).join('.');
          document.cookie = `googtrans=; path=/; domain=.${domainPart}; max-age=0; SameSite=Lax`;
        } catch (e) { /* ignore */ }
      }
    } else {
      const cookieValue = `/en/${langCode}`;
      document.cookie = `googtrans=${cookieValue}; path=/; max-age=31536000; SameSite=Lax`;
    }
    
    setShowLanguageSelector(false);
    
    // Google Translate applies reliably only when the page loads with the cookie set.
    // Reload once so the new language is applied (no timer loop, only on user selection).
    const skipReload =
      typeof window !== 'undefined' &&
      (window.__storyUploadOrModalInProgress || window.__messengerChatOpen);
    if (!skipReload) {
      setTimeout(() => window.location.reload(), 300);
    }
  };

  // Get current language display info
  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === selectedLanguage) || languages[0];
  };

  // Message notifications not displayed here; hook removed to avoid unused vars

  const normalizeSearchHistory = (history) => {
    if (!Array.isArray(history)) return [];
    return history
      .map((entry) => (entry?.user ? entry : { user: entry }))
      .filter((entry) => entry?.user?._id);
  };

  // Fetch search history on mount
  useEffect(() => {
    const fetchHistory = async () => {
      if (user && user.token) {
        const history = await getSearchHistory(user.token);
        setSearchHistory(normalizeSearchHistory(history));
      }
    };
    fetchHistory();
  }, [user]);

  // Initial notifications are now handled by the NotificationContext itself
  // No need to fetch in header component anymore

  // Close notification dropdown when clicking outside
  useClickOutside(notificationRef, () => {
    setShowNotifications(false);
  });

  // Close user menu dropdown when clicking outside
  useClickOutside(userMenuRef, () => {
    setShowUserMenu(false);
  }, USER_MENU_OUTSIDE_IGNORE_SELECTORS);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (e.target.value === "") setResults([]);
  };

  // Show live suggestions while typing (debounced).
  useEffect(() => {
    if (!user?.token) return;
    const query = searchQuery.trim();
    if (!query) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await search(query, user.token);
        const list = Array.isArray(res) ? res : [];
        const q = query.toLowerCase();
        const suggestions = list.filter((u) => {
          const first = String(u?.first_name || "").toLowerCase();
          const last = String(u?.last_name || "").toLowerCase();
          const full = `${first} ${last}`.trim();
          const username = String(u?.username || "").toLowerCase();
          return (
            first.startsWith(q) ||
            last.startsWith(q) ||
            full.startsWith(q) ||
            username.startsWith(q)
          );
        });
        setResults(suggestions);
      } catch (error) {
        console.error("Live search failed:", error);
        setResults([]);
      }
    }, 220);

    return () => clearTimeout(timer);
  }, [searchQuery, user?.token]);

  // Update search history after a search
  const handleSearchKeyUp = async (e) => {
    if (e.key === "Enter" && searchQuery.trim() !== "" && user && user.token) {
      const res = await search(searchQuery, user.token);
      setResults(res);
      // Refresh history after search
      const history = await getSearchHistory(user.token);
      setSearchHistory(normalizeSearchHistory(history));
    }
  };

  const isActive = (path) => {
    // Handle exact matches and sub-routes
    if (path === "/") {
      return currentPath === "/" || currentPath === "/home";
    }
    
    // Handle profile routes
    if (path === "/profile") {
      return currentPath.startsWith("/profile/");
    }
    
    // Handle post routes
    if (path === "/post") {
      return currentPath.startsWith("/post/");
    }
    
    // Handle other exact matches
    return currentPath === path || currentPath.startsWith(path + "/");
  };

  // Messenger toggle handler not used in this header variant

  // Dark mode removed: toggle logic has been removed

  const getHistoryUserId = (entry) => entry?.user?._id || entry?._id;

  const removeFromHistory = (id) => {
    if (!id) return;
    setSearchHistory((prev) =>
      prev.filter((entry) => String(getHistoryUserId(entry)) !== String(id))
    );
  };

  const handleSearchUserSelect = (selectedUserId, options = {}) => {
    const { mobile = false } = options;
    removeFromHistory(selectedUserId);
    setResults((prev) =>
      prev.filter((entry) => String(entry?._id) !== String(selectedUserId))
    );
    setSearchQuery("");
    setInputFocused(false);
    if (mobile) {
      setShowMobileSearch(false);
    }
  };

  // Mark all as read in backend
  const markAllNotificationsRead = async () => {
    if (!user || !user.token) return;
    try {
      await axios.put("/notifications/markAllRead", {}, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
    } catch (e) {}
  };

  // Mark all as read when opening dropdown
  const handleNotificationIconClick = async () => {
    if (isMobile) {
      setShowMobileNotifications(prev => !prev);
      // Prevent body scroll when notifications are open on mobile
      if (!showMobileNotifications) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'auto';
      }
    } else {
      setShowNotifications((prev) => !prev);
    }
    if (unreadCount > 0) {
      markAllAsRead();
      await markAllNotificationsRead();
    }
  };

  const showNotificationToast = (notif) => {
    const appLabel = 'Birthday Reminder 🎉';
    const message = formatMobileNotificationMessage(notif.message, appLabel);
    showToast({
      type: 'message',
      title: appLabel,
      message,
      duration: 6000,
    });
  };

  // Navigate to specific content when clicking notification
  const handleNotificationClick = (notif) => {
    if (notif.type === "friend_request") {
      navigate("/friends");
    } else if (notif.type === "comment" || notif.type === "like" || notif.type === "comment_reply" || notif.type === "friend_tag" || notif.type === "post" || notif.type === "group_post") {
      // Navigate to the specific post
      if (notif.postId) {
        navigate(`/post/${notif.postId}`);
      } else {
        // Fallback to custom toast if no postId
        showNotificationToast(notif);
      }
    } else if (notif.type === "live_stream") {
      if (notif.liveStreamId) {
        // Open live stream viewer
        setSelectedLiveStream(notif.liveStreamId);
      }
    } else {
      showNotificationToast(notif);
    }
    
    // Close the notification dropdown after clicking
    setShowNotifications(false);
  };

  // Handle friend request actions
  const handleFriendRequestAction = async (action, notificationId, senderId, senderName) => {
    if (!user || !user.token) return;
    try {
      // Ensure both IDs are strings
      const notificationIdStr = notificationId ? (typeof notificationId === 'string' ? notificationId : notificationId.toString()) : null;
      const senderIdStr = senderId ? (typeof senderId === 'string' ? senderId : senderId.toString()) : null;
      
      if (!notificationIdStr || !senderIdStr) {
        showError("❌ Unable to process: Invalid notification or sender information", 4000);
        return;
      }
      
      const endpoint = action === "accept" ? "/notification/acceptFriend" : "/notification/declineFriend";
      await axios.put(endpoint, {
        notificationId: notificationIdStr,
        senderId: senderIdStr
      }, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      
      // The notification will be updated via real-time events from the server
      // No need to update locally as it will come through Socket.IO
      
      // Show beautiful toast notification
      if (action === "accept") {
        showSuccess(`✅ You are now friends with ${senderName}!`, 4000);
      } else {
        showError(`❌ Friend request from ${senderName} declined`, 3000);
      }
      
    } catch (error) {
      console.error("Error handling friend request:", error);
      showError("❌ Failed to process friend request. Please try again.", 4000);
    }
  };

  
  return (
    <header className="header">
      {/* Left Section */}
      <div className="header_left">
        <Link to="/" className="header_logo" aria-label="Home">
          <div className="logo_container">
            <Logo />
            <span className="beta_version_text">Beta version</span>
          </div>
        </Link>
        {!isMobile && (
          <div
            className="search search1"
            aria-label="Search"
            style={{ position: "relative" }}
          >
            <Search color={color} />
            <input
              type="text"
              placeholder="Search"
              className="hide_input"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyUp={handleSearchKeyUp}
              autoComplete="off"
              ref={inputRef}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setTimeout(() => setInputFocused(false), 150)}
            />
            {/* Show results if present */}
            {results && results.length > 0 ? (
              <div
                className="search_results_popup scrollbar"
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  width: "100%",
                  background: "#fff",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                  zIndex: 100,
                  borderRadius: "8px",
                  marginTop: "4px",
                }}
              >
                {results.map((user) => (
                  <Link
                    to={`/profile/${user.username}`}
                    className="search_user_item hover1"
                    key={user._id}
                    onClick={() => {
                      handleSearchUserSelect(user._id);
                    }}
                  >
                    <img src={user.picture} alt="" />
                    <span>
                      {user.first_name} {user.last_name}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              // Show filtered history when input is focused
              inputFocused &&
              searchHistory &&
              searchHistory.length > 0 && (
                <div
                  className="search_results_popup scrollbar"
                  style={{ 
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    width: "100%",
                    background: "#fff",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    zIndex: 100,
                    borderRadius: "8px",
                    marginTop: "4px",
                  }}
                >
                  <div
                    className="search_history_header"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>Recent searches</span>
                  </div>
                  {/* Filter history by searchQuery for suggestions */}
                  {searchHistory
                    .filter((user) =>
                      `${user.user.first_name} ${user.user.last_name}`
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase())
                    )
                    .map((user) => (
                      <div
                        className="search_user_item hover1"
                        key={user.user._id}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Link
                          to={`/profile/${user.user.username}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flex: 1,
                          }}
                          onClick={() => {
                            handleSearchUserSelect(user.user._id);
                          }}
                        >
                          <img src={user.user.picture} alt="" />
                          <span>
                            {user.user.first_name} {user.user.last_name}
                          </span>
                        </Link>
                        <span
                          style={{
                            cursor: "pointer",
                            fontSize: "18px",
                            color: "#65676b",
                            marginLeft: "8px",
                            padding: "2px 6px",
                            borderRadius: "50%",
                            transition: "background 0.2s",
                          }}
                          
                          title="Remove from history"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={() => removeFromHistory(user.user._id)}
                        >
                          &times;
                        </span>
                      </div>
                    ))}
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Middle Section */}
      <div className="header_middle">
        <Link
          to="/"
          className={`middle_icon hover1 ${
            isActive("/") ? "active_header" : ""
          }`}
          aria-label="Home"
        >
          <HomeActive />
        </Link>

        <Link
          to="/friends"
          className={`middle_icon hover1 ${
            isActive("/friends") ? "active_header" : ""
          }`}
          aria-label="Friends"
        >
          <Friends color={isActive("/friends") ? "#ffffff" : "#000000"} />
        </Link>

        <Link
          to="/watch"
          className={`middle_icon hover1 ${
            isActive("/watch") ? "active_header" : ""
          }`}
          aria-label="Watch"
        >
          <Watch color={isActive("/watch") ? "#ffffff" : "#000000"} />
        </Link>

        <Link
          to="/market"
          className={`middle_icon hover1 ${
            isActive("/market") ? "active_header" : ""
          }`}
          aria-label="Marketplace"
        >
          <Market color={isActive("/market") ? "#ffffff" : "#000000"} />
        </Link>

        <Link
          to="/pricing"
          className={`header_text_nav hover1 ${
            isActive("/pricing") ? "active_header" : ""
          }`}
          aria-label="Pricing"
        >
          <span className="header_account_btn_text">Pricing</span>
        </Link>
      </div>

      {/* Right Section */}
      <div className="header_right">
        {/* Desktop Icons - Hidden on Mobile */}
        {!isMobile && (
          <>
            {/* Notification Icon with badge - Only show if user is logged in */}
            {user && (
            <div ref={notificationRef} style={{ position: "relative" }}>
              <span
                className={`circle_icon hover1 ${isActive("/notifications") ? "active_header" : ""}`}
                aria-label="Notifications"
                onClick={handleNotificationIconClick}
                tabIndex={0}
              >
                <Notifications color={color} />
                {unreadCount > 0 && (
                  <span 
                    className="header_badge"
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      background: unreadCount <= 3 ? "linear-gradient(135deg, #2ed573, #1e90ff)" :
                                 unreadCount <= 10 ? "linear-gradient(135deg, #ffa502, #ff6348)" :
                                 unreadCount <= 50 ? "linear-gradient(135deg, #ff4757, #ff3742)" :
                                 "linear-gradient(135deg, #ff3838, #ff0000)",
                      color: "#fff",
                      borderRadius: "12px",
                      fontSize: "11px",
                      padding: "3px 8px",
                      minWidth: 18,
                      height: 18,
                      textAlign: "center",
                      zIndex: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "700",
                      boxShadow: unreadCount <= 3 ? "0 2px 8px rgba(46, 213, 115, 0.3)" :
                                 unreadCount <= 10 ? "0 2px 8px rgba(255, 165, 2, 0.3)" :
                                 unreadCount <= 50 ? "0 2px 8px rgba(255, 71, 87, 0.3)" :
                                 "0 2px 8px rgba(255, 56, 56, 0.3)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      animation: unreadCount > 50 ? "urgentPulse 1s infinite" : "unreadPulse 2s infinite"
                    }}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </span>
              {/* Notification Dropdown */}
              {showNotifications && notifications && notifications.length > 0 && (
                <div
                  className="search_results_popup scrollbar"
                  style={{
                    position: "absolute",
                    top: "120%",
                    right: 0,
                    width: 360,
                    background: "#fff",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    zIndex: 200,
                    borderRadius: 8,
                    marginTop: 4,
                    maxHeight: 500,
                    overflowY: "auto",
                  }}
                >
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      // Clicking on header area closes the dropdown
                      setShowNotifications(false);
                    }}
                    style={{ 
                      padding: 12, 
                      borderBottom: "1px solid #eee", 
                      display: "flex", 
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer"
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>Notifications</span>
                    <button
                      onClick={() => {
                        navigate("/notifications");
                        setShowNotifications(false);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#1876f2",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: 600,
                        padding: "4px 8px",
                        borderRadius: "4px",
                        transition: "background 0.2s"
                      }}
                      onMouseEnter={(e) => e.target.style.background = "#f0f2f5"}
                      onMouseLeave={(e) => e.target.style.background = "none"}
                    >
                      See All
                    </button>
                  </div>
                  {notifications.slice(0, 5).map((notif) => (
                    <div
                      key={notif._id}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        padding: 12,
                        background: notif.isRead ? "#f7f7f7" : "#e7f3ff",
                        borderBottom: "1px solid #f0f0f0",
                        transition: "background-color 0.2s ease",
                      }}
                    >
                      <img
                        src={notif.from?.picture || "/images/default_pic.png"}
                        alt="profile"
                        style={{ width: 40, height: 40, borderRadius: "50%", marginRight: 10 }}
                      />
                      <div style={{ flex: 1, display: "flex", alignItems: "flex-start", gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div 
                            style={{ 
                              cursor: notif.type !== "friend_request" ? "pointer" : "default",
                              fontWeight: notif.isRead ? 400 : 600 
                            }}
                            onClick={() => {
                              if (notif.type !== "friend_request") {
                                handleNotificationClick(notif);
                              }
                            }}
                          >
                            {notif.message}
                          </div>
                          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>
                                {notif.from?.first_name} {notif.from?.last_name}
                                {notif.type === "like" && notif.reactionType && (
                                  <span style={{ marginLeft: 5 }}>
                                    {notif.reactionType === "like" && <span className="notranslate">👍</span>}
                                    {notif.reactionType === "love" && <span className="notranslate">❤️</span>}
                                    {notif.reactionType === "haha" && <span className="notranslate">😂</span>}
                                    {notif.reactionType === "wow" && <span className="notranslate">😮</span>}
                                    {notif.reactionType === "sad" && <span className="notranslate">😢</span>}
                                    {notif.reactionType === "angry" && <span className="notranslate">😡</span>}
                                  </span>
                                )}
                              </span>
                              <span style={{ fontSize: 10, color: "#999" }}>
                                {formatRelativeTime(notif.createdAt)}
                              </span>
                            </div>
                            {(notif.type === "comment" || notif.type === "like" || notif.type === "comment_reply") && (
                              <span style={{ fontSize: 10, color: "#1876f2" }}>
                                • Click to view post
                              </span>
                            )}
                          </div>
                          {notif.postText && (notif.type === "comment" || notif.type === "like") && (
                            <div style={{
                              fontSize: 11,
                              color: "#666",
                              marginTop: 4,
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }}>
                              Post: {notif.postText}
                            </div>
                          )}
                          {/* Friend request action buttons */}
                          {notif.type === "friend_request" && (notif.friendRequestStatus === "pending" || !notif.friendRequestStatus) && (
                            <div style={{ 
                              display: "flex", 
                              gap: 8, 
                              marginTop: 8 
                            }}>
                              <button
                                onClick={() => {
                                  // Ensure senderId is always a string
                                  const senderIdRaw = notif.from?._id || notif.from;
                                  const senderId = senderIdRaw ? (typeof senderIdRaw === 'string' ? senderIdRaw : senderIdRaw.toString()) : null;
                                  const senderName = notif.from?.first_name && notif.from?.last_name 
                                    ? `${notif.from.first_name} ${notif.from.last_name}` 
                                    : 'User';
                                  if (!senderId) {
                                    showError("❌ Unable to process: Invalid sender information", 4000);
                                    return;
                                  }
                                  handleFriendRequestAction("accept", notif._id, senderId, senderName);
                                }}
                                style={{
                                  background: "#1876f2",
                                  color: "white",
                                  border: "none",
                                  padding: "6px 12px",
                                  borderRadius: 6,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  transition: "background 0.2s"
                                }}
                                onMouseEnter={(e) => e.target.style.background = "#166fe5"}
                                onMouseLeave={(e) => e.target.style.background = "#1876f2"}
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => {
                                  // Ensure senderId is always a string
                                  const senderIdRaw = notif.from?._id || notif.from;
                                  const senderId = senderIdRaw ? (typeof senderIdRaw === 'string' ? senderIdRaw : senderIdRaw.toString()) : null;
                                  const senderName = notif.from?.first_name && notif.from?.last_name 
                                    ? `${notif.from.first_name} ${notif.from.last_name}` 
                                    : 'User';
                                  if (!senderId) {
                                    showError("❌ Unable to process: Invalid sender information", 4000);
                                    return;
                                  }
                                  handleFriendRequestAction("decline", notif._id, senderId, senderName);
                                }}
                                style={{
                                  background: "#e4e6ea",
                                  color: "#1c1e21",
                                  border: "none",
                                  padding: "6px 12px",
                                  borderRadius: 6,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  transition: "background 0.2s"
                                }}
                                onMouseEnter={(e) => e.target.style.background = "#d8dadf"}
                                onMouseLeave={(e) => e.target.style.background = "#e4e6ea"}
                              >
                                Decline
                              </button>
                            </div>
                          )}
                          {notif.type === "friend_request" && notif.friendRequestStatus === "accepted" && (
                            <div style={{ 
                              marginTop: 6, 
                              color: "#42b883", 
                              fontSize: 12, 
                              fontWeight: 600 
                            }}>
                              ✓ Request accepted
                            </div>
                          )}
                          {notif.type === "friend_request" && notif.friendRequestStatus === "declined" && (
                            <div style={{ 
                              marginTop: 6, 
                              color: "#999", 
                              fontSize: 12, 
                              fontWeight: 600 
                            }}>
                              Request declined
                            </div>
                          )}
                        </div>
                        {/* Post image next to text and before action buttons */}
                        {(notif.type === "comment" || notif.type === "like") && notif.postImage && (
                          notif.postMediaType === "video" ? (
                            <div style={{
                              position: "relative",
                              width: 60,
                              height: 60,
                              borderRadius: 8,
                              overflow: "hidden",
                              border: "1px solid #e9ecef",
                              cursor: "pointer",
                              flexShrink: 0,
                              background: "#000"
                            }}
                            onClick={() => handleNotificationClick(notif)}>
                              <video
                                src={notif.postImage}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                                muted
                                preload="metadata"
                              />
                              <div style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                color: "white",
                                fontSize: "16px",
                                textShadow: "0 0 5px rgba(0,0,0,0.7)",
                                pointerEvents: "none"
                              }}>
                                ▶
                              </div>
                            </div>
                          ) : (
                            <img
                              src={notif.postImage}
                              alt="post"
                              style={{
                                width: 60,
                                height: 60,
                                borderRadius: 8,
                                objectFit: "cover",
                                border: "1px solid #e9ecef",
                                cursor: "pointer",
                                flexShrink: 0
                              }}
                              onClick={() => handleNotificationClick(notif)}
                            />
                          )
                        )}
                      </div>
                    </div>
                  ))}
                  {notifications.length > 5 && (
                    <div 
                      onClick={() => setShowNotifications(false)}
                      style={{ 
                        padding: 12, 
                        textAlign: "center", 
                        borderTop: "1px solid #eee",
                        background: "#f8f9fa",
                        color: "#65676b",
                        fontSize: "14px",
                        cursor: "pointer"
                      }}
                    >
                    </div>
                  )}
                  {notifications.length === 0 && (
                    <div 
                      onClick={() => setShowNotifications(false)}
                      style={{ 
                        padding: 16, 
                        textAlign: "center", 
                        color: "#888",
                        cursor: "pointer"
                      }}
                    >
                      No notifications
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Language Selector */}
            <div ref={languageSelectorRef} style={{ position: "relative" }} className="notranslate">
              <div
                className={`circle_icon language_selector_button notranslate ${showLanguageSelector ? 'active_header' : ''}`}
                onClick={() => setShowLanguageSelector(!showLanguageSelector)}
                aria-label="Language"
                title={`Current Language: ${getCurrentLanguage().name}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "0 12px",
                  minWidth: "auto",
                  width: "auto",
                  borderRadius: "20px",
                }}
              >
                <span className="notranslate" style={{ 
                  fontSize: "14px", 
                  fontWeight: 500,
                  color: "#050505",
                  whiteSpace: "nowrap",
                  maxWidth: "150px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                  {getCurrentLanguage().name}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: "2px" }}>
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </div>
              {showLanguageSelector && (
                <div
                  className="language_selector_dropdown notranslate"
                  style={{
                    position: "absolute",
                    top: "120%",
                    right: 0,
                    width: 200,
                    background: "#fff",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    zIndex: 200,
                    borderRadius: 8,
                    marginTop: 4,
                    maxHeight: 400,
                    overflowY: "auto",
                    padding: "8px 0",
                  }}
                >
                  <div className="notranslate" style={{ padding: "8px 16px", borderBottom: "1px solid #eee", fontWeight: 600, fontSize: "14px" }}>
                    Select Language
                  </div>
                  {languages.map((lang) => (
                    <div
                      key={lang.code}
                      className="notranslate"
                      onClick={() => handleLanguageChange(lang.code)}
                      style={{
                        padding: "10px 16px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        transition: "background 0.2s",
                        background: selectedLanguage === lang.code ? "#e7f3ff" : "transparent",
                        fontWeight: selectedLanguage === lang.code ? 600 : 400,
                      }}
                      onMouseEnter={(e) => {
                        if (selectedLanguage !== lang.code) {
                          e.target.style.background = "#f0f2f5";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedLanguage !== lang.code) {
                          e.target.style.background = "transparent";
                        } else {
                          e.target.style.background = "#e7f3ff";
                        }
                      }}
                    >
                      <span className="notranslate" style={{ fontSize: "14px", color: "#050505", flex: 1 }}>{lang.name}</span>
                      {selectedLanguage === lang.code && (
                        <span className="notranslate" style={{ fontSize: "16px", color: "#1876f2" }}>✓</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Hidden Google Translate element */}
            <div id="google_translate_element_header" style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}></div>

            {/* User Menu - Only show if user is logged in */}
            {user && (
            <div
              ref={userMenuRef}
              style={{ position: "relative" }}
            >
              <div
                className={`circle_icon hover1 ${showUserMenu ? "active_header" : ""}`}
                onClick={() => setShowUserMenu((prev) => !prev)}
                aria-label="User Menu"
              >
                <ArrowDown />
              </div>
              {showUserMenu && <UserMenu user={user} onMobileNavigation={null} />}
            </div>
            )}
            {/* Login link for non-logged in users */}
            {!user && (
              <Link
                to="/login"
                className="blue_btn"
                style={{ padding: "8px 16px", fontSize: "14px", textDecoration: "none" }}
              >
                Log In
              </Link>
            )}
          </>
        )}

        {/* Mobile Three Dots Menu */}
        {isMobile && (
          <div className="mobile_menu_container">
            {/* Search Icon with Text */}
            <div
              className="mobile_search_button"
              onClick={() => {
                setShowMobileMenu(false);
                setShowMobileSearch(true);
              }}
            >
              <Search color={color} />
              <span className="mobile_search_text">Search UsmaApp</span>
            </div>

            {/* Notifications Icon moved to header side (visible) - Only show if user is logged in */}
            {user && (
            <div
              className="circle_icon hover1"
              onClick={() => {
                // close the dropdown if open and open mobile notifications
                setShowMobileMenu(false);
                handleNotificationIconClick();
              }}
              aria-label="Notifications"
              title="Notifications"
            >
              <Notifications color={color} />
              {unreadCount > 0 && (
                <span
                  className="mobile_badge"
                  style={{
                    position: "absolute",
                    top: -5,
                    right: -5,
                    background: "linear-gradient(135deg, #ff4757, #ff3742)",
                    color: "#fff",
                    borderRadius: "50%",
                    fontSize: "10px",
                    padding: "2px 4px",
                    minWidth: 16,
                    height: 16,
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "700",
                  }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            )}

            {/* Messenger Icon for Mobile - Only show if user is logged in */}
            {user && (
            <div
              className="circle_icon hover1"
              onClick={() => window.dispatchEvent(new CustomEvent('toggleMessenger'))}
              aria-label="Messenger"
              title="Open Messenger"
              style={{ position: "relative" }}
            >
              <Messenger color={color} />
              {unreadMessageCount > 0 && (
                <span className="mobile_messenger_badge" title={`${unreadMessageCount} unread message${unreadMessageCount !== 1 ? 's' : ''}`}>
                  {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                </span>
              )}
              </div>
            )}

            {/* Language Icon for Mobile */}
            <div ref={mobileLanguageSelectorRef} style={{ position: "relative" }} className="notranslate">
              <div
                className={`circle_icon ${showLanguageSelector ? 'active_header' : ''}`}
                onClick={() => {
                  setShowMobileMenu(false);
                  setShowLanguageSelector(!showLanguageSelector);
                }}
                aria-label="Language"
                title={`Current Language: ${getCurrentLanguage().name}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px"
                }}
              >
                🌐
              </div>
              {showLanguageSelector && (
                <div
                  className="language_selector_dropdown notranslate"
                  style={{
                    position: "absolute",
                    top: "120%",
                    right: 0,
                    width: 200,
                    background: "#fff",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    zIndex: 2000,
                    borderRadius: 8,
                    marginTop: 4,
                    maxHeight: 400,
                    overflowY: "auto",
                    padding: "8px 0",
                  }}
                >
                  <div className="notranslate" style={{ padding: "8px 16px", borderBottom: "1px solid #eee", fontWeight: 600, fontSize: "14px" }}>
                    Select Language
                  </div>
                  {languages.map((lang) => (
                    <div
                      key={lang.code}
                      className="notranslate"
                      onClick={() => handleLanguageChange(lang.code)}
                      style={{
                        padding: "10px 16px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        transition: "background 0.2s",
                        background: selectedLanguage === lang.code ? "#e7f3ff" : "transparent",
                        fontWeight: selectedLanguage === lang.code ? 600 : 400,
                      }}
                      onMouseEnter={(e) => {
                        if (selectedLanguage !== lang.code) {
                          e.target.style.background = "#f0f2f5";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedLanguage !== lang.code) {
                          e.target.style.background = "transparent";
                        } else {
                          e.target.style.background = "#e7f3ff";
                        }
                      }}
                    >
                      <span className="notranslate" style={{ fontSize: "14px", color: "#050505", flex: 1 }}>{lang.name}</span>
                      {selectedLanguage === lang.code && (
                        <span className="notranslate" style={{ fontSize: "16px", color: "#1876f2" }}>✓</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Three Dots Menu */}
            <div ref={mobileMenuRef} style={{ position: "relative" }}>
              <div
                className="circle_icon hover1 mobile_menu_trigger"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                aria-label="Mobile Menu"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="6" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="12" cy="18" r="2" />
                </svg>
              </div>

              {/* Mobile Menu Dropdown (Notifications removed from here) */}
              {showMobileMenu && (
                <div className="mobile_menu_dropdown">
                  <Link
                    to="/pricing"
                    className="mobile_menu_item"
                    style={{ textDecoration: "none", color: "inherit" }}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <span>Pricing</span>
                  </Link>

                  {/* Dark Mode removed from mobile menu */}

                  {/* User Menu - Only show if user is logged in */}
                  {user && (
                  <div 
                    className="mobile_menu_item"
                    onClick={() => {
                      setShowMobileUserMenu(true);
                      setShowMobileMenu(false);
                      // Prevent body scroll when user menu is open on mobile
                      document.body.style.overflow = 'hidden';
                    }}
                  >
                    <ArrowDown />
                    <span>Menu</span>
                  </div>
                  )}
                  {/* Login link for non-logged in users */}
                  {!user && (
                    <Link
                      to="/login"
                      className="mobile_menu_item"
                      style={{ textDecoration: "none", color: "inherit" }}
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <span>Log In</span>
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Mobile User Menu - Only show if user is logged in */}
            {showUserMenu && user && (
              <div className="mobile_user_menu">
                <UserMenu user={user} />
              </div>
            )}
          </div>
        )}

        {/* Mobile Search Bar - Show when search button is clicked */}
        {isMobile && showMobileSearch && (
          <div className="mobile_search_container" ref={mobileSearchRef}>
            <div className="mobile_search_header">
              <div className="mobile_search_input_wrapper">
                <Search color={color} />
                <input
                  type="text"
                  placeholder="Search UsmaApp"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyUp={handleSearchKeyUp}
                  autoComplete="off"
                  ref={inputRef}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setTimeout(() => setInputFocused(false), 150)}
                  className="mobile_search_input"
                />
                <button
                  className="mobile_search_close"
                  onClick={() => {
                    setShowMobileSearch(false);
                    setSearchQuery("");
                    setResults([]);
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
            
            {/* Mobile Search Results */}
            {results && results.length > 0 && (
              <div className="mobile_search_results">
                {results.map((user) => (
                  <Link
                    to={`/profile/${user.username}`}
                    className="mobile_search_user_item"
                    key={user._id}
                    onClick={() => {
                      handleSearchUserSelect(user._id, { mobile: true });
                    }}
                  >
                    <img src={user.picture} alt="" />
                    <div className="mobile_search_user_info">
                      <span className="mobile_search_user_name">
                        {user.first_name} {user.last_name}
                      </span>
                      <span className="mobile_search_user_username">
                        @{user.username}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
            
            {/* Mobile Search History */}
            {searchHistory && searchHistory.length > 0 && !searchQuery && (
              <div className="mobile_search_history">
                <div className="mobile_search_history_header">
                  <span>Recent Searches</span>
                  <button
                    onClick={() => {
                      // Clear search history
                      setSearchHistory([]);
                    }}
                    className="mobile_clear_history"
                  >
                    Clear All
                  </button>
                </div>
                {searchHistory.map((user, i) => (
                  <Link
                    to={`/profile/${user.username}`}
                    className="mobile_search_user_item"
                    key={i}
                    onClick={() => {
                      handleSearchUserSelect(user._id, { mobile: true });
                    }}
                  >
                    <img src={user.picture} alt="" />
                    <div className="mobile_search_user_info">
                      <span className="mobile_search_user_name">
                        {user.first_name} {user.last_name}
                      </span>
                      <span className="mobile_search_user_username">
                        @{user.username}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Mobile Notification Overlay */}
        {isMobile && showMobileNotifications && (
          <div className="mobile_notification_overlay">
            <div className="mobile_notification_panel">
              <div className="mobile_notification_header">
                <h2>Notifications</h2>
                <div className="mobile_notification_actions">
                  <button
                    onClick={() => {
                      navigate("/notifications");
                      setShowMobileNotifications(false);
                      document.body.style.overflow = 'auto';
                    }}
                    className="mobile_see_all_btn"
                  >
                    See All
                  </button>
                  <button
                    onClick={() => {
                      setShowMobileNotifications(false);
                      document.body.style.overflow = 'auto';
                    }}
                    className="mobile_close_btn"
                    aria-label="Close notifications"
                  >
                    ✕
                  </button>
                </div>
              </div>
              


              <div className="mobile_notification_content">
                <div className="mobile_notification_list">
                  {notifications && notifications.length > 0 ? (
                    notifications
                      .slice(0, 10)
                      .map((notif) => (
                        <div
                          key={notif._id}
                          className={`mobile_notification_item ${!notif.isRead ? 'mobile_unread' : ''}`}
                          style={{
                            background: notif.isRead ? "#f7f7f7" : "#e7f3ff",
                            cursor: notif.type !== "friend_request" ? "pointer" : "default",
                          }}
                          onClick={() => {
                            if (notif.type !== "friend_request") {
                              handleNotificationClick(notif);
                              setShowMobileNotifications(false);
                              document.body.style.overflow = 'auto';
                            }
                          }}
                        >
                          <img
                            src={notif.from?.picture || "/images/default_pic.png"}
                            alt="profile"
                            className="mobile_notification_avatar"
                          />
                          <div className="mobile_notification_content_inner">
                            <div className="mobile_notification_message">
                              {formatMobileNotificationMessage(notif.message)}
                            </div>
                            <div className="mobile_notification_meta">
                              <span className="mobile_notification_user">
                                {notif.from?.first_name} {notif.from?.last_name}
                              </span>
                              <span className="mobile_notification_time">
                                {formatRelativeTime(notif.createdAt)}
                              </span>
                            </div>
                            {/* Friend request action buttons */}
                            {notif.type === "friend_request" && (notif.friendRequestStatus === "pending" || !notif.friendRequestStatus) && (
                              <div className="mobile_friend_request_actions">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Ensure senderId is always a string
                                    const senderIdRaw = notif.from?._id || notif.from;
                                    const senderId = senderIdRaw ? (typeof senderIdRaw === 'string' ? senderIdRaw : senderIdRaw.toString()) : null;
                                    const senderName = notif.from?.first_name && notif.from?.last_name 
                                      ? `${notif.from.first_name} ${notif.from.last_name}` 
                                      : 'User';
                                    if (!senderId) {
                                      showError("❌ Unable to process: Invalid sender information", 4000);
                                      return;
                                    }
                                    handleFriendRequestAction("accept", notif._id, senderId, senderName);
                                  }}
                                  className="mobile_accept_btn"
                                >
                                  Accept
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Ensure senderId is always a string
                                    const senderIdRaw = notif.from?._id || notif.from;
                                    const senderId = senderIdRaw ? (typeof senderIdRaw === 'string' ? senderIdRaw : senderIdRaw.toString()) : null;
                                    const senderName = notif.from?.first_name && notif.from?.last_name 
                                      ? `${notif.from.first_name} ${notif.from.last_name}` 
                                      : 'User';
                                    if (!senderId) {
                                      showError("❌ Unable to process: Invalid sender information", 4000);
                                      return;
                                    }
                                    handleFriendRequestAction("decline", notif._id, senderId, senderName);
                                  }}
                                  className="mobile_decline_btn"
                                >
                                  Decline
                                </button>
                              </div>
                            )}
                            {notif.type === "friend_request" && notif.friendRequestStatus === "accepted" && (
                              <div className="mobile_request_accepted">
                                ✓ Request accepted
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="mobile_no_notifications">
                        <div className="mobile_no_notifications_icon">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                          </svg>
                        </div>
                        <h3>No notifications</h3>
                        <p>When you get likes, comments, or friend requests, they'll appear here</p>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile User Menu Overlay */}
        {isMobile && showMobileUserMenu && (
          <div className="mobile_user_menu_overlay">
            <div className="mobile_user_menu_panel">
              <div className="mobile_user_menu_header">
                <h2>Menu</h2>
                <button
                  onClick={() => {
                    setShowMobileUserMenu(false);
                    document.body.style.overflow = 'auto';
                  }}
                  className="mobile_close_btn"
                  aria-label="Close menu"
                >
                  ✕
                </button>
              </div>
              <div className="mobile_user_menu_content">
                <UserMenu 
                  user={user} 
                  onMobileNavigation={() => {
                    setShowMobileUserMenu(false);
                    document.body.style.overflow = 'auto';
                  }}
                  renderAfterHeader={null}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Live Stream Viewer Modal */}
      {selectedLiveStream && (
        <LiveStreamViewer
          streamId={selectedLiveStream}
          onClose={() => setSelectedLiveStream(null)}
        />
      )}
    </header>
  );
}
